import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ArrowLeft } from 'react-bootstrap-icons';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSafeReturnPath, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { conversationBankSearchPath } from '../../helpers/conversationBank';
import { isAdmin } from '../../helpers/roleHelpers';
import Loader from '../Loader';
import ConversationBankFlow from './ConversationBankFlow';
import './conversationBank.scss';

const ConversationBankFlowPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const contactId = queryParams.get('contactId') || '';
  const returnTo = getSafeReturnPath(queryParams.get('returnTo')) || (contactId ? `/crm/${contactId}` : '');
  const highlightBranchId = (location.hash || '').replace('#branch-', '');

  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [placeholderContext, setPlaceholderContext] = useState({});
  const [lead, setLead] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { response, error } = await makeApiRequests({
        endpoint: `${ENDPOINTS.CONVERSATION_BANK_WITH_ID(id)}${
          contactId ? `?contactId=${encodeURIComponent(contactId)}` : ''
        }${isAdmin() ? `${contactId ? '&' : '?'}includeInactive=true` : ''}`,
        method: 'GET'
      });
      setLoading(false);
      if (error) {
        toast.error(error);
        return;
      }
      setRecord(response);
      setPlaceholderContext(response.placeholderContext || {});
      setLead(response.lead || null);
    };
    load();
  }, [id, contactId]);

  useEffect(() => {
    if (!id) return;
    makeApiRequests({
      endpoint: ENDPOINTS.CONVERSATION_BANK_USAGE,
      requestBody: {
        eventType: 'opened',
        record: id,
        contactId: contactId || undefined
      }
    });
  }, [id, contactId]);

  const trackUsage = async ({ eventType = 'copied', record, branch, responseType }) => {
    await makeApiRequests({
      endpoint: ENDPOINTS.CONVERSATION_BANK_USAGE,
      requestBody: {
        eventType,
        record: record || id,
        branch: branch || undefined,
        responseType,
        contactId: contactId || undefined
      }
    });
  };

  const backToSearch = () => {
    history.push(conversationBankSearchPath({ contactId, returnTo }));
  };

  if (loading) {
    return (
      <div className="cb-page px-3 py-3">
        <Loader />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="cb-page px-3 py-3">
        <Button variant="link" className="px-0" onClick={backToSearch}>
          <ArrowLeft className="mr-1" />
          Back to search
        </Button>
        <div>Conversation was not found.</div>
      </div>
    );
  }

  return (
    <div className="cb-page px-3 pb-5 pt-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <Button variant="link" className="px-0" onClick={backToSearch}>
          <ArrowLeft className="mr-1" />
          Back to search
        </Button>
        {lead && returnTo && (
          <Button size="sm" variant="outline-dark" onClick={() => history.push(returnTo)}>
            Return to lead
          </Button>
        )}
      </div>
      {lead && (
        <div className="cb-lead-banner rounded-border p-3 mb-3">
          <div className="tinyFont text-muted text-uppercase">Attached lead</div>
          <div className="font-weight-bold">{lead.buyer}</div>
          <div className="tinyFont text-muted mt-1 mb-0">
            Edit the copied language before you send it. The Conversation Bank never sends messages.
          </div>
        </div>
      )}
      <ConversationBankFlow
        record={record}
        placeholderContext={placeholderContext}
        highlightBranchId={highlightBranchId}
        onCopied={meta => trackUsage(meta)}
      />
    </div>
  );
};

export default ConversationBankFlowPage;
